import { motion } from "framer-motion";
import { Ticket, KeyRound, CalendarCheck, ShieldAlert, LockKeyholeOpen, Check } from "lucide-react";

const rand = (n) => Array.from({ length: n }, () => Math.floor(Math.random() * 10)).join("");

// Build a UI-ready action from a voice tool call (mirrors backend build_demo_action)
export function makeDemoAction(name, args = {}) {
  const a = args || {};
  switch (name) {
    case "open_ticket":
      return { kind: "ticket", title: "Maintenance ticket opened", status: "Synced to TownSq",
        detail: [a.issue || "Maintenance request", a.location].filter(Boolean).join(" · "), ref: "#" + rand(4) };
    case "unlock_amenity": {
      const am = a.amenity || "gate";
      return { kind: "unlock", title: `${am.charAt(0).toUpperCase() + am.slice(1)} unlocked`, status: "Relocks in 60s",
        detail: "Access granted · entry logged" };
    }
    case "rsvp_event":
      return { kind: "rsvp", title: "RSVP confirmed", status: "Added to guest list",
        detail: `${a.event || "the event"} · party of ${a.party_size || 1}`, ref: "#" + rand(4) };
    case "report_security":
      return { kind: "security", title: "Safety report filed", status: "Sent to Redline monitoring",
        detail: [a.concern || "Safety concern", a.location].filter(Boolean).join(" · "), ref: "#" + rand(4) };
    case "send_access_code": {
      const am = a.amenity || "amenity";
      return { kind: "access_code", title: `${am.charAt(0).toUpperCase() + am.slice(1)} access code sent`,
        status: "Texted to your phone · expires 24h", detail: "Use at the keypad", code: rand(4) };
    }
    default:
      return null;
  }
}

const ICONS = {
  ticket: Ticket,
  unlock: LockKeyholeOpen,
  rsvp: CalendarCheck,
  security: ShieldAlert,
  access_code: KeyRound,
};

export function DemoActionCard({ action, dark = false }) {
  if (!action) return null;
  const Icon = ICONS[action.kind] || Check;
  return (
    <motion.div
      data-testid={`demo-action-${action.kind}`}
      initial={{ opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={`mt-2 rounded-xl border overflow-hidden ${dark
        ? "border-emerald-400/30 bg-emerald-400/10"
        : "border-emerald-200 bg-emerald-50"}`}>
      <div className={`h-0.5 w-full bg-gradient-to-r from-emerald-400 to-emerald-500`} />
      <div className="px-2.5 py-2">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 shrink-0 rounded-full bg-emerald-500 flex items-center justify-center">
            <Icon className="h-3.5 w-3.5 text-white" strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1">
            <div className={`text-[11px] font-extrabold leading-tight ${dark ? "text-emerald-100" : "text-emerald-900"}`}>{action.title}</div>
            {action.detail && <div className={`text-[10px] leading-tight truncate ${dark ? "text-emerald-200/80" : "text-emerald-700"}`}>{action.detail}</div>}
          </div>
          {action.ref && (
            <span className={`shrink-0 text-[10px] font-bold rounded-md px-1.5 py-0.5 ${dark ? "bg-white/10 text-emerald-100" : "bg-white text-emerald-700 border border-emerald-200"}`}>{action.ref}</span>
          )}
          {action.code && (
            <span className={`shrink-0 text-[12px] font-extrabold tracking-widest rounded-md px-2 py-0.5 ${dark ? "bg-white/10 text-white" : "bg-white text-ink border border-emerald-200"}`}>{action.code}</span>
          )}
        </div>
        {action.status && (
          <div className={`mt-1.5 flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide ${dark ? "text-emerald-300" : "text-emerald-600"}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> {action.status}
          </div>
        )}
      </div>
    </motion.div>
  );
}
